import { $t } from '@previo/core'
import { computed } from 'vue'
import { CountryId } from '@previo/codebooks'
import { PSettingItem } from '@previo/design-system'
import { Flag, useFeatureFlags } from '@previo/feature-flags'
import { Routes } from '~/router/routes'
import { usePropertyStore } from '~/store/property/property'
import { ModuleCheck, PrivilegeCheck } from '~/composables/usePermissionCheck'
import { Module } from '~/codebooks/acl/Module'
import { usePropertyModuleStore } from '~/store/property/modules'
import { useCountryStrategy } from '~/pages/setting/applications/accounting/exports/software/i10n/useCountryStrategy'
import { Privilege } from '~/codebooks/acl/Privilege'

export const settingModules = computed<PSettingItem[]>(() => {
  const modules: PSettingItem[] = []

  const propertyModuleStore = usePropertyModuleStore()
  const propertyStore = usePropertyStore()
  const ff = useFeatureFlags()

  const hasReservationSettingsEnabled = computed(
    () => ff.isEnabled(Flag.PMS_SETTINGS_RESERVATION_ENABLED).value,
  )
  const isExchangeRatesEnabled = computed(() => ff.isEnabled(Flag.PMS_EXCHANGE_RATES).value)
  const isTaxSettingsEnabled = computed(() => ff.isEnabled(Flag.PMS_TAX_SETTINGS_ENABLED).value)
  const isServiceMappingEnabled = computed(
    () => ff.isEnabled(Flag.PMS_SERVICE_MAPPING_ENABLED).value,
  )
  const isConnectedRoomsVisible = computed(
    () =>
      ff.isEnabled(Flag.PMS_CONNECTED_ROOMS_ENABLED).value &&
      ModuleCheck.hasAccess(Module.SETTINGS_HOTEL_ROOMS),
  )

  const softwareExportCountryStrategy = useCountryStrategy(propertyStore.getCountryId())

  const isCzProperty = propertyStore.getCountryId() === CountryId.CZE

  modules.push(
    new PSettingItem('hotel', $t('Hotel'), 'hotel', null, true, [
      new PSettingItem(
        'hotel-email',
        $t('E-mail'),
        'envelope',
        Routes.SETTINGS_HOTEL_EMAIL,
        ModuleCheck.hasAccess(Module.SETTINGS_EMAIL_SETTINGS),
      ),
      new PSettingItem(
        'hotel-whatsapp',
        $t('WhatsApp'),
        'brand whatsapp',
        Routes.SETTINGS_HOTEL_WHATSAPP,
        ff.isEnabled(Flag.PMS_COMMUNICATION_WHATSAPP_ENABLED).value &&
          ModuleCheck.hasAccess(Module.SETTINGS_EMAIL_SETTINGS),
      ),
      new PSettingItem(
        'hotel-reports',
        $t('Reports'),
        'chart-pie',
        null,
        isCzProperty && ModuleCheck.hasAccess(Module.REPORTS_FOR_STAT_AUTH),
        [
          new PSettingItem(
            'hotel-reports-csu',
            $t('ČSÚ'),
            'chart-simple',
            Routes.SETTINGS_HOTEL_REPORTS_CSU,
            isCzProperty && ModuleCheck.hasAccess(Module.REPORTS_FOR_STAT_AUTH),
          ),
        ],
      ),
    ]),
    new PSettingItem(
      'reservations',
      $t('Reservations'),
      'calendar-lines',
      null,
      hasReservationSettingsEnabled.value && ModuleCheck.hasAccess(Module.SETTINGS_SYSTEM),
      [
        new PSettingItem(
          'reservation-general',
          $t('General'),
          'info',
          Routes.SETTINGS_RESERVATIONS_GENERAL,
          PrivilegeCheck.hasPrivilege(Privilege.CANVAS_SETTING_EDIT),
        ),
        new PSettingItem(
          'reservation-voucher',
          $t('Voucher'),
          'envelope',
          Routes.SETTINGS_RESERVATIONS_VOUCHER,
          PrivilegeCheck.hasPrivilege(Privilege.PRICING_SETUP),
        ),
        new PSettingItem(
          'reservation-folio',
          $t('Folio'),
          'coins',
          Routes.SETTINGS_RESERVATIONS_FOLIO,
          PrivilegeCheck.hasPrivilege(Privilege.CANVAS_SETTING_EDIT),
        ),
      ],
    ),
    new PSettingItem('prices', $t('Prices'), 'coins', null, null, [
      new PSettingItem(
        'prices-currencies',
        $t('Currencies & exchange rates'),
        'exchange',
        Routes.EXCHANGE_RATES_INDEX,
        isExchangeRatesEnabled.value &&
          ModuleCheck.hasAccess(Module.PRICES_CURRENCIES_EXCHANGE_RATES),
      ),
      new PSettingItem(
        'prices-visitor-tax',
        $t('Taxes & fees'),
        'coins',
        Routes.VISITOR_TAX_INDEX,
        isTaxSettingsEnabled.value && ModuleCheck.hasAccess(Module.PRICES_TAX_SETTINGS),
      ),
    ]),

    new PSettingItem('accounting', $t('Accounting'), 'file-invoice', null, true, [
      new PSettingItem(
        'documents',
        $t('Documents'),
        'file-invoice',
        Routes.SETTINGS_ACCOUNTING_DOCUMENTS_INDEX,
        true,
      ),
      new PSettingItem(
        'gift-vouchers',
        $t('Gift vouchers'),
        'gift',
        Routes.SETTINGS_ACCOUNTING_GIFT_VOUCHERS_INDEX,
        ff.isEnabled(Flag.INVOICING_GIFT_VOUCHERS_ENABLED).value &&
          ModuleCheck.hasAccess(Module.PAYMENTS_GIFT_VOUCHERS),
      ),
      new PSettingItem(
        'rounding',
        $t('Rounding'),
        'money-bill',
        Routes.SETTINGS_ACCOUNTING_ROUNDING_INDEX,
        true,
      ),
      new PSettingItem(
        'payments',
        $t('Payments numbering'),
        'money-bill',
        Routes.SETTINGS_ACCOUNTING_PAYMENTS_INDEX,
        true,
      ),
      new PSettingItem(
        'accepted-payments-types',
        $t('Accepted payments types'),
        'money-bill-wave',
        Routes.SETTINGS_ACCOUNTING_ACCEPTED_PAYMENTS_TYPES_INDEX,
        true,
      ),
      new PSettingItem('exports', $t('Exports'), 'print', null, true, [
        new PSettingItem(
          'accounting-softwares',
          $t('Accounting softwares'),
          'gear',
          Routes.SETTINGS_ACCOUNTING_EXPORTS_SOFTWARE_INDEX,
          softwareExportCountryStrategy.allowedSoftware.length !== 0,
        ),
        new PSettingItem(
          'vat-breakdown',
          $t('VAT breakdown'),
          'list-ul',
          Routes.SETTINGS_ACCOUNTING_EXPORTS_VAT_BREAKDOWN_INDEX,
          ModuleCheck.hasAccess(Module.PAYMENTS_VAT_BREAKDOWN),
        ),
      ]),
    ]),
    new PSettingItem(
      'offers',
      $t('Offers'),
      'hand-holding-dollar',
      Routes.SETTINGS_OFFER_INDEX,
      true,
      [],
    ),
    new PSettingItem('guests-and-companies', $t('Guests & Companies'), 'users', null, true, [
      new PSettingItem(
        'guests-and-companies-general',
        $t('General'),
        'info',
        Routes.SETTINGS_GUESTS_AND_COMPANIES_GENERAL_INDEX,
        true,
      ),
      new PSettingItem(
        'guests-and-companies-guest-labels',
        $t('Guest labels'),
        'tag',
        Routes.SETTINGS_GUESTS_AND_COMPANIES_GUEST_LABELS_INDEX,
        true,
      ),
      new PSettingItem(
        'guests-and-companies-company-labels',
        $t('Company labels'),
        'tags',
        Routes.SETTINGS_GUESTS_AND_COMPANIES_COMPANY_LABELS_INDEX,
        true,
      ),
      new PSettingItem(
        'guests-and-companies-agency',
        $t('Agency system'),
        'handshake-angle',
        Routes.SETTINGS_GUESTS_AND_COMPANIES_AGENCY_INDEX,
        !propertyModuleStore.isOwnerSystemActive() ||
          (propertyModuleStore.isOwnerSystemActive() &&
            ModuleCheck.hasAccess(Module.COMPANIES_AGENCY_TAB)),
      ),
    ]),
    new PSettingItem('housekeeping', $t('Housekeeping'), 'vacuum', null, null, [
      new PSettingItem(
        'housekeeping-general',
        $t('General'),
        'info',
        Routes.SETTINGS_HOUSEKEEPING_GENERAL_INDEX,
        ModuleCheck.hasAccess(Module.HOUSEKEEPING_CLEANING_GRID),
      ),
      new PSettingItem(
        'housekeeping-app',
        $t('Housekeeping app'),
        'vacuum',
        Routes.SETTINGS_HOUSEKEEPING_APP_INDEX,
        ModuleCheck.hasAccess(Module.HOUSEKEEPING_CLEANING_GRID),
      ),
    ]),
    new PSettingItem(
      'reservation-plus',
      $t('Reservation +'),
      'calendar-day',
      null,
      ModuleCheck.hasAccess(Module.ONLINE_RESERVATION_BOOKING),
      [
        new PSettingItem(
          'reservation-plus-general',
          $t('General'),
          'info',
          Routes.SETTINGS_ONLINE_RESERVATIONS_BOOKING_GENERAL,
          ModuleCheck.hasAccess(Module.ONLINE_RESERVATION_BOOKING),
        ),
        new PSettingItem(
          'reservation-plus-code-gen',
          $t('Code generator'),
          'browser',
          Routes.SETTINGS_ONLINE_RESERVATIONS_BOOKING_FORM_CONFIGURATION,
          ModuleCheck.hasAccess(Module.ONLINE_RESERVATION_BOOKING),
        ),
        new PSettingItem(
          'reservation-plus-conv',
          $t('Conversions'),
          'chart-simple',
          Routes.SETTINGS_ONLINE_RESERVATIONS_BOOKING_CONVERSION,
          ModuleCheck.hasAccess(Module.ONLINE_RESERVATION_BOOKING),
        ),
      ],
    ),
    new PSettingItem('alfred', $t('Alfred app'), 'user-tag', null, true, [
      new PSettingItem(
        'alfred-general',
        $t('General'),
        'info',
        Routes.SETTINGS_ALFRED_GENERAL_INDEX,
        true,
      ),
      new PSettingItem(
        'alfred-tips',
        $t('Activities'),
        'comment-medical',
        Routes.SETTINGS_ALFRED_TRIP_TIPS,
        true,
      ),
    ]),
    new PSettingItem(
      'lock-systems',
      $t('Lock systems'),
      'key',
      null,
      ModuleCheck.hasAccess(Module.SETTINGS_LOCK_SYSTEMS_SETUP),
      [
        new PSettingItem(
          'lock-systems-setup',
          $t('Connection set-up'),
          'link',
          Routes.SETTINGS_LOCK_SYSTEMS_SETUP,
          ModuleCheck.hasAccess(Module.SETTINGS_LOCK_SYSTEMS_SETUP),
        ),
        new PSettingItem(
          'lock-systems-settings',
          $t('Settings'),
          'gear',
          Routes.SETTINGS_LOCK_SYSTEMS_SETTINGS,
          ModuleCheck.hasAccess(Module.SETTINGS_LOCK_SYSTEMS),
        ),
      ],
    ),
    new PSettingItem(
      'lock-systems-settings',
      $t('Lock systems'),
      'key',
      Routes.SETTINGS_LOCK_SYSTEMS_SETTINGS,
      !ModuleCheck.hasAccess(Module.SETTINGS_LOCK_SYSTEMS_SETUP) &&
        ModuleCheck.hasAccess(Module.SETTINGS_LOCK_SYSTEMS),
    ),
    new PSettingItem(
      'self-service-bar',
      $t('Self service bar'),
      'qrcode',
      null,
      ModuleCheck.hasAccess(Module.SETTINGS_SELF_SERVICE_BAR),
      [
        new PSettingItem(
          'self-service-bar-general',
          $t('General'),
          'info',
          Routes.SELF_SERVICE_BAR_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_SELF_SERVICE_BAR),
        ),
      ],
    ),
    new PSettingItem('channel-manager', $t('Channel manager'), 'download', null, true, [
      new PSettingItem(
        'channel-manager-my-connections',
        $t('My connections'),
        'link',
        Routes.SETTINGS_CHANNEL_MANAGER_MY_CONNECTIONS_INDEX,
        true,
      ),
      new PSettingItem(
        'channel-manager-service-mapping',
        $t('Service mapping'),
        'arrow-right-arrow-left',
        Routes.SETTINGS_CHANNEL_MANAGER_SERVICE_MAPPING_INDEX,
        isServiceMappingEnabled.value,
      ),
      new PSettingItem(
        'channel-manager-marketplace',
        $t('Marketplace'),
        'cart-shopping',
        Routes.SETTINGS_CHANNEL_MANAGER_MARKETPLACE_INDEX,
        true,
      ),
    ]),
    new PSettingItem(
      'channel-manager',
      $t('Channel manager'),
      'download',
      null,
      false, // not visible until feature release
      [
        new PSettingItem(
          'my-connections',
          $t('My connections'),
          'link',
          Routes.SETTINGS_CHANNEL_MANAGER_MY_CONNECTIONS_INDEX,
          true,
        ),
      ],
    ),
    new PSettingItem(
      'pos',
      $t('POS'),
      'cash-register',
      null,
      ModuleCheck.hasAccess(Module.SETTINGS_POS),
      [
        new PSettingItem(
          'pos-general',
          $t('General'),
          'info',
          Routes.POS_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_POS),
        ),
      ],
    ),
    new PSettingItem(
      'connected-rooms',
      $t('Connected rooms'),
      'link',
      null,
      isConnectedRoomsVisible.value,
      [
        new PSettingItem(
          'connected-rooms-connections',
          $t('Connections'),
          'list',
          Routes.SETTINGS_CONNECTED_ROOMS_CONNECTIONS_INDEX,
          isConnectedRoomsVisible.value,
        ),
        new PSettingItem(
          'connected-rooms-general',
          $t('General'),
          'gear',
          Routes.SETTINGS_CONNECTED_ROOMS_GENERAL_INDEX,
          isConnectedRoomsVisible.value,
        ),
      ],
    ),
    new PSettingItem('notifications', $t('Notifications'), 'bell', null, true, [
      new PSettingItem(
        'notifications-reservation',
        $t('Reservation'),
        'calendar-lines',
        Routes.SETTINGS_NOTIFICATIONS_RESERVATION,
        true,
      ),
    ]),
  )

  return modules
})
